import type { GameInfo } from '@/features/games/shared/types/game-info';

export type GamesSearchError = 'searchError';
export type GamesSearchStatus = 'idle' | 'loading' | 'success' | 'error';

// Fetch state is keyed by the term it belongs to, so the derived output below can
// tell "results for the current term" from "stale results for a previous term".
export type FetchState =
  | { kind: 'idle' }
  | { kind: 'loading'; term: string }
  | { kind: 'success'; term: string; games: GameInfo[] }
  | { kind: 'error'; term: string };

export type SearchOutput = {
  status: GamesSearchStatus;
  error: GamesSearchError | null;
  results: GameInfo[];
};

export const EMPTY_RESULTS: GameInfo[] = [];

// Derive the public search output from the term-keyed fetch state. When the search
// is inactive, or the stored state belongs to a previous term (a fetch is pending),
// report idle/loading + empty results instead of flashing stale data.
export function deriveSearchOutput(
  fetchState: FetchState,
  canSearch: boolean,
  trimmedSearchTerm: string,
): SearchOutput {
  if (!canSearch) {
    return { status: 'idle', error: null, results: EMPTY_RESULTS };
  }

  // No settled state for the current term yet -> a fetch is pending.
  if (fetchState.kind === 'idle' || fetchState.term !== trimmedSearchTerm) {
    return { status: 'loading', error: null, results: EMPTY_RESULTS };
  }

  if (fetchState.kind === 'success') {
    return { status: 'success', error: null, results: fetchState.games };
  }

  if (fetchState.kind === 'error') {
    return { status: 'error', error: 'searchError', results: EMPTY_RESULTS };
  }

  return { status: 'loading', error: null, results: EMPTY_RESULTS };
}
